// Library Methods
import { DateTime } from "luxon";
import {
  createContext,
  useCallback,
  useState,
  useMemo,
  useContext,
  useEffect,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";

// Utils
import useClinic from "../hooks/useClinic";
import { ToastContext } from "./ToastContext";
import { linkExamToDevice } from "../services/Device";
import { useAddExams } from "../hooks/exams/useAddExams";
import { useDefaultExams } from "../hooks/exams/useDefaultExams";
import { useEditExams } from "../hooks/exams/useEditExams";
import { useDuplicateExams } from "../hooks/exams/useDuplicateExams";
import { useAddEditExamHelpers } from "../hooks/exams/useAddEditExamHelpers";
import { examCardEnums } from "../enums";

const ExamListContext = createContext();

export const useExamListContext = () => {
  const context = useContext(ExamListContext);
  if (!context) {
    throw new Error(
      "useExamListContext must be used within ExamListContextProvider"
    );
  }

  return context;
};

export const ExamListProvider = ({
  devicesWithExams = [],
  devicesOptions = [],
  rows = [],
  setRows = () => {},
  refetchExams = async () => {},
  refetchPatients = async () => {},
  defaultProps, // used mainly for testing
  children,
}) => {
  const { clinicId, clinicTimezone } = useClinic();
  const { t } = useTranslation();
  const { getAccessTokenSilently } = useAuth0();
  const { toast, setToast } = useContext(ToastContext);

  const { defaultExamOrder, getDeselectedExamOrder, generateExamsToEdit } =
    useAddEditExamHelpers();
  const { addExam } = useAddExams();
  const { addDefaultExam } = useDefaultExams();
  const { editExam } = useEditExams();
  const { duplicateExam } = useDuplicateExams();

  // states init
  const today = DateTime.now().setZone(clinicTimezone).startOf("day");
  const [scheduledDate, setScheduledDate] = useState(today);
  const [selectedExam, setSelectedExam] = useState("");
  const [editExamId, setEditExamId] = useState("");
  const [editExamDevice, setEditExamDevice] = useState({ name: "", val: "" });

  const [examCards, setExamCards] = useState(() => {
    const selected = defaultProps?.selectedExamCards ?? [];
    const deselected = defaultExamOrder.filter(
      (defaultExam) =>
        !selected.some(
          (selectedExam) =>
            defaultExam.algorithmPath === selectedExam.algorithmPath
        )
    );
    return {
      selected: [...selected], // in order of selection
      deselected: [...deselected], // in default exam order
    };
  });

  const [scheduledError, setScheduledError] = useState(false);

  // states for add / edit modals
  const [addModal, setAddModal] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [duplicateModal, setDuplicateModal] = useState(false);
  const [selectedDuplicateExam, setSelectedDuplicateExam] = useState(undefined);

  // state to check if unavailableDeviceModal should be displayed or not
  const [unavailableDeviceModal, setUnavailableDeviceModal] = useState(false);
  const [linkDeviceModal, setLinkDeviceModal] = useState(false);

  // loading states
  const [linkLoading, setLinkLoading] = useState(false);
  const [isSavingExam, setIsSavingExam] = useState(false);

  const sectionTypeOrder = useMemo(() => {
    const enumValues = Object.values(examCardEnums);
    return examCards.selected.map((exam) => {
      return enumValues.find(
        (enumValue) => enumValue.algorithmPath === exam.examFieldType
      )?.type;
    });
  }, [examCards.selected]);

  // clear add form
  const clearForm = useCallback(() => {
    setExamCards({
      selected: [],
      deselected: [...defaultExamOrder],
    });
    setEditExamDevice({ name: "", val: "" });
    setScheduledError(false);
  }, [defaultExamOrder]);

  const onEditExamCallback = async (editExamId) => {
    if (editExamDevice.val !== "") {
      await handleLinkDevice(editExamId);
      setEditExamDevice("");
    }

    setToast(() => ({
      success: true,
      message: t("exams_edit_success"),
    }));

    setIsSavingExam(true);
    await refetchPatients();
    await refetchExams();
    setIsSavingExam(false);

    // close modal
    setEditModal(false);

    // clear form
    clearForm();
  };

  const onDuplicateExamCallback = async (newExam) => {
    setToast(() => ({
      success: true,
      message: t("exams_add_success"),
    }));
    setSelectedDuplicateExam(undefined);

    // add to table
    const newRows = [...rows];
    newRows.unshift(newExam);
    setRows(newRows);

    setIsSavingExam(true);
    await refetchPatients();
    await refetchExams();
    setIsSavingExam(false);

    // close modal
    setDuplicateModal(false);

    // clear form
    clearForm();
  };

  const onAddDefaultExamCallback = async (newExam) => {
    setToast(() => ({
      success: true,
      message: t("exams_add_success"),
    }));

    // add to table
    const newRows = [...rows];
    newRows.unshift(newExam);
    setRows(newRows);

    setIsSavingExam(true);
    await refetchPatients();
    await refetchExams();
    setIsSavingExam(false);

    if (availableDevicesOptions?.length === 1) {
      handleLinkDeviceWithoutModal(newExam.id, availableDevicesOptions[0].id);
    }
  };

  const onAddExamCallback = async (newExam) => {
    setToast(() => ({
      success: true,
      message: t("exams_add_success"),
    }));

    // add to table
    const newRows = [...rows];
    newRows.unshift(newExam);
    setRows(newRows);

    //link exam to device (if selected)
    if (editExamDevice.val !== "") {
      handleLinkDevice(newExam?.id);
    }

    setIsSavingExam(true);
    await refetchPatients();
    await refetchExams();
    setIsSavingExam(false);

    // close modal
    setAddModal(false);

    // clear form
    clearForm();
  };

  const availableDevicesOptions = useMemo(() => {
    if (!devicesOptions?.length) return [];
    else
      return devicesOptions.filter(
        (option) => option.webserviceCompatible === true
      );
  }, [devicesOptions]);

  // handles the link device modal confirmation
  const handleLinkDevice = useCallback(
    async (id) => {
      // close the unlink Modal
      setLinkDeviceModal(false);
      setLinkLoading(true);

      // link
      try {
        const token = await getAccessTokenSilently();
        const examData = { examId: selectedExam.id ?? id };
        await linkExamToDevice(token, clinicId, editExamDevice.val, examData);

        setSelectedExam("");
        setToast(() => ({
          success: true,
          message: t("device_link_success"),
        }));
        setTimeout(() => {
          setLinkLoading(false);
        }, 2000);
      } catch (error) {
        setSelectedExam("");
        setToast(() => ({
          success: false,
          message: t("device_link_error"),
        }));
        setLinkLoading(false);
      }
    },
    [
      getAccessTokenSilently,
      selectedExam.id,
      clinicId,
      editExamDevice,
      setToast,
      t,
    ]
  );

  // handles the device linking when there is only one device option
  const handleLinkDeviceWithoutModal = useCallback(
    async (examId, deviceId) => {
      try {
        // get token
        const token = await getAccessTokenSilently();
        const examData = { examId };
        await linkExamToDevice(token, clinicId, deviceId, examData);

        setSelectedExam("");
        setToast(() => ({
          success: true,
          message: t("device_link_success"),
        }));
      } catch (error) {
        setSelectedExam("");
        setToast(() => ({
          success: false,
          message: t("device_link_error"),
        }));
      }
    },
    [getAccessTokenSilently, clinicId, setToast, t]
  );

  // handle link device click
  const handleLinkDeviceClick = useCallback(
    (exam) => {
      setEditExamDevice({ name: "", val: "" });
      setSelectedExam(exam);

      // in case there are no option, show unavailable modal
      if (availableDevicesOptions.length <= 0) {
        setUnavailableDeviceModal(true);
      }
      // in case there are many options, show modal with available options
      else if (availableDevicesOptions.length > 1) {
        setLinkDeviceModal(true);
      }
      // in case there's only one device, we link it directly
      // and the device is not expired for the clinic assignment
      else {
        handleLinkDeviceWithoutModal(exam.id, availableDevicesOptions[0].id);
      }
    },
    [availableDevicesOptions, handleLinkDeviceWithoutModal]
  );

  const handleUnavailableDeviceClose = (event, reason) => {
    if (reason !== "backdropClick") {
      setUnavailableDeviceModal(false);
    }
  };

  // handle on close of link device modal
  const handleLinkDeviceClose = (event, reason) => {
    if (reason !== "backdropClick") {
      setSelectedExam("");
      setLinkDeviceModal(false);
    }
  };

  // open the add exam modal
  const handleAddOpen = () => {
    clearForm();
    setAddModal(true);
  };

  // close the add exam modal
  const handleAddClose = () => {
    setAddModal(false);
    clearForm();
  };

  // open the duplicate exam modal
  const handleDuplicateOpen = useCallback((exam) => {
    setSelectedDuplicateExam(exam);
    setDuplicateModal(true);
  }, []);

  // close the duplicate exam modal
  const handleDuplicateClose = () => {
    setDuplicateModal(false);
    setSelectedDuplicateExam(undefined);
  };

  // open the edit exam modal
  const handleEditOpen = useCallback(
    (exam) => {
      clearForm();
      setSelectedExam(exam);
      setEditExamId(exam.id);

      // use the scheduled date at midnight
      // (prevents the date from rolling over)
      if (exam.scheduledDate) {
        const utcString = DateTime.fromISO(exam.scheduledDate, { zone: "utc" });
        const localString = utcString.setZone(clinicTimezone);
        setScheduledDate(localString);
      }

      const selectedExamCards = generateExamsToEdit({ exam });
      const deselectedExamCards = getDeselectedExamOrder({ exam });

      setExamCards({
        selected: [...selectedExamCards],
        deselected: [...deselectedExamCards],
      });

      // open modal
      setEditModal(true);
    },
    [getDeselectedExamOrder, generateExamsToEdit, clearForm, clinicTimezone]
  );

  // close the edit exam modal
  const handleEditClose = () => {
    setEditModal(false);
    clearForm();
  };

  // refetch exams when devicesWithExams length has changed (when an exam gets completed, this happens)
  useEffect(() => {
    const controller = new AbortController();
    refetchExams(controller.signal);
    return () => {
      controller.abort();
    };
  }, [devicesWithExams?.length, refetchExams]);

  return (
    <ExamListContext.Provider
      value={{
        addModal,
        editModal,
        duplicateModal,
        editExamId,
        isSavingExam,
        sectionTypeOrder,
        defaultExamOrder,
        scheduledDate,
        scheduledError,
        availableDevicesOptions,
        linkLoading,
        linkDeviceModal,
        unavailableDeviceModal,
        editExamDevice,
        selectedExam,
        examCards,
        selectedDuplicateExam,
        devicesOptions,
        devicesWithExams: defaultProps?.devicesWithExams ?? devicesWithExams,
        rows: defaultProps?.rows ?? rows,
        toast,
        actions: {
          setRows,
          setToast,
          setEditModal,
          setSelectedExam,
          setExamCards,
          setIsSavingExam,
          setScheduledDate,
          setScheduledError,
          setEditExamDevice,
          setLinkDeviceModal,
          setUnavailableDeviceModal,
          refetchExams,
          refetchPatients,
          handleUnavailableDeviceClose,
          handleLinkDeviceClose,
          handleLinkDevice,
          handleLinkDeviceWithoutModal,
          handleLinkDeviceClick,
          handleAddOpen,
          handleAddClose,
          handleEditOpen,
          handleEditClose,
          handleDuplicateOpen,
          handleDuplicateClose,
          addExam,
          duplicateExam,
          editExam,
          addDefaultExam: defaultProps?.addDefaultExam ?? addDefaultExam,
          onAddExamCallback,
          onEditExamCallback,
          onAddDefaultExamCallback:
            defaultProps?.onAddDefaultExamCallback ?? onAddDefaultExamCallback,
          onDuplicateExamCallback,
        },
      }}
    >
      {children}
    </ExamListContext.Provider>
  );
};
